import Icon from '@atoms/Icon/Icon';
import LoadingSpinner from '@atoms/LoadingSpinner/LoadingSpinner';
import { IconName } from '@models/IIcon';
import { ILink } from '@models/ILink';
import { IDidYouMeanResults, IListingResponse } from '@models/IListingResponse';
import { ISearchBlock } from '@models/ISearchBlock';
import { ISearchResult } from '@models/ISearchResult';
import ErrorBannerNotificationEnum from '@models/enums/ErrorBannerNotificationEnum';
import DidYouMeanResults from '@molecules/DidYouMeanResults/DidYouMeanResults';
import SearchBar from '@molecules/SearchBar/SearchBar';
import SearchSummaryResults from '@molecules/SearchSummaryResults/SearchSummaryResults';
import useErrorNotificationStore from '@state/ErrorBannerNotificationDisplayStore';
import useAriaLabels from '@utils/hooks/useAriaLabels';
import useResizeObserver from '@utils/hooks/useResizeObserver';
import useSearchSummaryResults from '@utils/hooks/useSearchSummaryResults';
import axios from 'axios';
import classNames from 'classnames';
import { useState } from 'react';
import PopularSearches from './PopularSearches';
import SearchResultsList from './SearchResultsList';

interface Props {
  readonly culture: string;
  readonly endpoint: string;
  readonly isZoomedDesktop: boolean;
  readonly listingPage: string;
  readonly onCloseCallback: () => void;
  readonly overlaySearchIcon: IconName;
  readonly popularSearches?: { title: string; links: ILink[] };
  readonly searchBlock?: ISearchBlock;
  readonly searchPlaceholder: string;
  readonly searchSubTitle: string;
  readonly suggestedSearchTitleLabel: string;
}

const Search = ({
  culture,
  endpoint,
  isZoomedDesktop,
  listingPage,
  onCloseCallback,
  overlaySearchIcon,
  popularSearches,
  searchBlock,
  searchPlaceholder,
  searchSubTitle,
  suggestedSearchTitleLabel,
}: Props) => {
  const [value, setValue] = useState('');
  const [results, setResults] = useState<ISearchResult[]>([]);
  const [didYouMeanResults, setDidYouMeanResults] =
    useState<IDidYouMeanResults>({});
  const [searchBarContainerRef, { clientHeight: searchBarContainerHeight }] =
    useResizeObserver();
  const { setErrorBannerNotification } = useErrorNotificationStore();
  const { overlayToCloseDesktopNavigationAriaLabel } = useAriaLabels();

  const fetchResults = async (searchValue: string) => {
    try {
      const { data } = await axios.get<IListingResponse | undefined>(endpoint, {
        params: { pageSize: 10, query: searchValue, language: culture },
      });

      setResults((data?.items ?? []) as ISearchResult[]);
      setDidYouMeanResults(data?.didYouMean ?? ({} as IDidYouMeanResults));
    } catch {
      setErrorBannerNotification(ErrorBannerNotificationEnum.SearchResults);
    }
  };

  const { isLoading, searchSummaryResults, fetchSearchSummaryResults } =
    useSearchSummaryResults(searchBlock?.searchSummary ?? []);

  const handleDidYouMeanSearch = (searchTerm: string) => {
    setValue(searchTerm);
    fetchResults(searchTerm);
  };

  return (
    <>
      <div
        ref={searchBarContainerRef}
        className="relative flex h-[24%] min-h-[140px] w-full items-center justify-start bg-grey-200 md:h-[48%] md:min-h-[190px] md:pb-7.5"
        data-testid="CT-48-SearchBar-Container"
      >
        {isZoomedDesktop && (
          <button
            className="fixed top-2.5 right-2.5"
            onClick={onCloseCallback}
            aria-label={overlayToCloseDesktopNavigationAriaLabel}
          >
            <Icon name="close" />
          </button>
        )}
        <div className="container relative max-md:mt-1">
          <SearchBar
            searchSubTitle={searchSubTitle}
            searchPlaceholder={searchPlaceholder}
            overlaySearchIcon={overlaySearchIcon}
            listingPage={searchBlock?.searchSummaryPageLink ?? listingPage}
            value={value}
            setValue={setValue}
            onTypingDone={() => {
              if (value.length >= 1) {
                if (searchBlock) fetchSearchSummaryResults(value);
                else fetchResults(value);
              }
            }}
          />
        </div>
      </div>

      <div
        style={
          searchBlock
            ? { minHeight: `calc(100% - ${searchBarContainerHeight}px)` }
            : { height: `calc(100% - ${searchBarContainerHeight}px)` }
        }
        className={classNames(
          'w-full bg-white',
          searchBlock ? 'flex' : 'overflow-y-auto overscroll-y-none'
        )}
      >
        {!value && popularSearches && (
          <div className="container grid grid-cols-12 pt-5 lg:pt-[70px]">
            <PopularSearches
              title={popularSearches.title}
              links={popularSearches.links}
            />
          </div>
        )}

        {!!didYouMeanResults?.suggestions?.length && (
          <div className="container pt-10">
            <DidYouMeanResults
              {...didYouMeanResults}
              searchCallback={handleDidYouMeanSearch}
            />
          </div>
        )}
        {!!value &&
          !searchBlock &&
          results.some((result) => result.title && result.url) && (
            <div className="container">
              <SearchResultsList
                isZoomedDesktop={isZoomedDesktop}
                results={results}
                suggestedSearchTitleLabel={suggestedSearchTitleLabel}
              />
            </div>
          )}

        {value && isLoading && searchBlock && (
          <div className="flex w-full items-center justify-center">
            <LoadingSpinner />
          </div>
        )}

        {value && !isLoading && searchBlock && (
          <div className="w-full">
            <SearchSummaryResults
              searchSummaryResults={searchSummaryResults}
              searchValue={value}
              productsStatusLabels={searchBlock.statusLabels}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default Search;
